import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { ClientQuoteDetailsService } from './service/clientQuoteDetailsService';
import ClientQuoteDetailsView from './views/ClientQuoteDetailsView';
import { normalizeText } from '../../shared/utils/textNormalizationHelpers';
import { getQuotationStatusVariant } from '../../shared/utils/quotationStatusHelpers';
import {
  RefusalMessageContainer,
  RefusalMessageDescription,
  RefusalMessageError,
  RefusalTextarea,
} from './styles';

const ClientQuoteDetailsPage = ({ match, currentLanguage }) => {
  const clientLang = currentLanguage?.bepiService?.client || {};
  const sharedLang = currentLanguage?.bepiService?.shared || {};

  const steps = useMemo(() => ([
    clientLang.client_quote_details_step_summary,
    clientLang.client_quote_details_step_shipping,
    clientLang.client_quote_details_step_invoices,
    clientLang.client_quote_details_step_checkout,
  ]), [
    clientLang.client_quote_details_step_checkout,
    clientLang.client_quote_details_step_invoices,
    clientLang.client_quote_details_step_shipping,
    clientLang.client_quote_details_step_summary,
  ]);

  const [quoteData, setQuoteData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentStep, setCurrentStep] = useState(0);
  const [shippingConfirmed, setShippingConfirmed] = useState(false);
  const [notaRemessaFile, setNotaRemessaFile] = useState(null);

  const refusalTypeRef = useRef(null);
  const refusalReasonRef = useRef('');

  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    confirmText: clientLang.client_quote_details_confirm_btn,
    cancelText: sharedLang.shared_details_cancel_btn,
  });

  const quotationId = match?.params?.id;

  useEffect(() => {
    setModalConfig((previous) => ({
      ...previous,
      confirmText: previous.confirmText || clientLang.client_quote_details_confirm_btn,
      cancelText: previous.cancelText || sharedLang.shared_details_cancel_btn,
    }));
  }, [
    clientLang.client_quote_details_confirm_btn,
    sharedLang.shared_details_cancel_btn,
  ]);

  const closeModal = useCallback(() => {
    setModalConfig((previous) => ({ ...previous, isOpen: false }));
  }, []);

  const showModal = useCallback((config) => {
    setModalConfig({
      isOpen: true,
      type: config.type || 'info',
      title: config.title || '',
      message: config.message || '',
      onConfirm: config.onConfirm || null,
      onCancel: config.onCancel === null ? null : closeModal,
      confirmText: config.confirmText || clientLang.client_quote_details_confirm_btn,
      cancelText: config.cancelText || sharedLang.shared_details_cancel_btn,
    });
  }, [
    clientLang.client_quote_details_confirm_btn,
    closeModal,
    sharedLang.shared_details_cancel_btn,
  ]);

  const fetchQuoteDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!quotationId) {
        throw new Error(clientLang.client_quote_details_id_error);
      }

      const response = await ClientQuoteDetailsService.getQuoteDetails(quotationId);
      setQuoteData(response);
    } catch (requestError) {
      setError(requestError?.message || clientLang.client_quote_details_load_error);
    } finally {
      setIsLoading(false);
    }
  }, [
    clientLang.client_quote_details_id_error,
    clientLang.client_quote_details_load_error,
    quotationId,
  ]);

  useEffect(() => {
    fetchQuoteDetails();
  }, [fetchQuoteDetails]);

  const hasCheckoutActions = useCallback((data) => {
    const estimateStatus = normalizeText(data?.estimativa?.status);
    const budgetStatus = normalizeText(data?.orcamento?.status);
    return estimateStatus === 'estimativa entregue' || budgetStatus === 'pendente de aprovacao';
  }, []);

  const canShowEstimateActions = useMemo(
    () => normalizeText(quoteData?.estimativa?.status) === 'estimativa entregue',
    [quoteData]
  );

  const canShowBudgetActions = useMemo(
    () => normalizeText(quoteData?.orcamento?.status) === 'pendente de aprovacao',
    [quoteData]
  );

  const statusVariant = useMemo(() => {
    const sourceStatus = quoteData?.estimativa?.status || quoteData?.orcamento?.status;
    return getQuotationStatusVariant(sourceStatus);
  }, [quoteData]);

  const isEstimateDelivered = useMemo(
    () => normalizeText(quoteData?.estimativa?.status) === 'estimativa entregue',
    [quoteData]
  );

  // Status pós-aceitação (para mostrar orientações de envio inline no Step 0)
  const POST_ACCEPTANCE_STATUSES = [
    'aguardando equipamento',
    'equipamento recebido',
    'aguardando orcamento',
    'aprovado para manutencao',
    'autorizar manutencao',
    'aguardando inspeção e laudo',
    'aguardando faturamento',
    'pendente de aprovacao',
    'pronto para retirada',
    'equipamento em analise',
    'enviado'
  ];

  const isPostAcceptance = useMemo(() => {
    const status = normalizeText(quoteData?.estimativa?.status);
    return POST_ACCEPTANCE_STATUSES.includes(status);
  }, [quoteData]);

  // Fluxo travado quando não é estimativa entregue (comportamento original)
  const isFlowLocked = useMemo(
    () => Boolean(quoteData) && !isEstimateDelivered,
    [isEstimateDelivered, quoteData]
  );

  const flowLockMessage = clientLang.client_quote_details_flow_lock_message;

  useEffect(() => {
    if (isFlowLocked && currentStep !== 0) {
      setCurrentStep(0);
    }
  }, [currentStep, isFlowLocked]);

  const validateCurrentStep = useCallback(() => {
    if (currentStep === 0) return Boolean(quoteData?.estimativa);
    if (currentStep === 1) return Boolean(shippingConfirmed);
    if (currentStep === 2) return Boolean(notaRemessaFile) && hasCheckoutActions(quoteData);
    return true;
  }, [currentStep, hasCheckoutActions, notaRemessaFile, quoteData, shippingConfirmed]);

  const getCurrentValidationMessage = useCallback(() => {
    if (currentStep === 0) return clientLang.client_quote_details_validation_summary;
    if (currentStep === 1) return clientLang.client_quote_details_validation_shipping;
    if (currentStep === 2) {
      if (!hasCheckoutActions(quoteData)) return clientLang.client_quote_details_validation_checkout_unavailable;
      return 'Anexe a nota de remessa para continuar.';
    }
    return clientLang.client_quote_details_validation_generic;
  }, [
    clientLang.client_quote_details_validation_checkout_unavailable,
    clientLang.client_quote_details_validation_generic,
    clientLang.client_quote_details_validation_shipping,
    clientLang.client_quote_details_validation_summary,
    currentStep,
    hasCheckoutActions,
    quoteData,
  ]);

  const handleAcceptEstimate = useCallback(() => {
    const estimateId = quoteData?.estimativa?.id;
    if (!estimateId) return;
    if (!notaRemessaFile) {
      showModal({
        type: 'warning',
        title: clientLang.client_quote_details_warning_title,
        message: 'Anexe a nota de remessa antes de aceitar a estimativa.',
        confirmText: clientLang.client_quote_details_ok_btn,
        onConfirm: closeModal,
        onCancel: null,
      });
      return;
    }

    showModal({
      type: 'success',
      title: clientLang.client_quote_details_estimate_accept_title,
      message: clientLang.client_quote_details_estimate_accept_text,
      confirmText: clientLang.client_quote_details_estimate_accept_confirm,
      onConfirm: async () => {
        try {
          await ClientQuoteDetailsService.acceptEstimate(estimateId, notaRemessaFile);
          closeModal();
          setNotaRemessaFile(null);
          await fetchQuoteDetails();
          showModal({
            type: 'success',
            title: clientLang.client_quote_details_estimate_accept_success_title,
            message: clientLang.client_quote_details_estimate_accept_success_text,
            confirmText: clientLang.client_quote_details_ok_btn,
            onConfirm: closeModal,
            onCancel: null,
          });
        } catch (_requestError) {
          closeModal();
          showModal({
            type: 'error',
            title: sharedLang.shared_details_error_label,
            message: clientLang.client_quote_details_estimate_accept_error,
            confirmText: clientLang.client_quote_details_ok_btn,
            onConfirm: closeModal,
            onCancel: null,
          });
        }
      },
    });
  }, [
    clientLang.client_quote_details_estimate_accept_confirm,
    clientLang.client_quote_details_estimate_accept_error,
    clientLang.client_quote_details_estimate_accept_success_text,
    clientLang.client_quote_details_estimate_accept_success_title,
    clientLang.client_quote_details_estimate_accept_text,
    clientLang.client_quote_details_estimate_accept_title,
    clientLang.client_quote_details_ok_btn,
    clientLang.client_quote_details_warning_title,
    closeModal,
    fetchQuoteDetails,
    notaRemessaFile,
    quoteData,
    sharedLang.shared_details_error_label,
    showModal,
  ]);

  const buildRefusalMessage = useCallback((showError = false) => (
    <RefusalMessageContainer>
      <RefusalMessageDescription>
        {clientLang.client_quote_details_refuse_reason_text}
      </RefusalMessageDescription>
      <RefusalTextarea
        rows={4}
        placeholder={clientLang.client_quote_details_refuse_reason_placeholder}
        onChange={(event) => {
          refusalReasonRef.current = event.target.value;
        }}
      />
      {showError ? (
        <RefusalMessageError>
          {clientLang.client_quote_details_refuse_reason_required}
        </RefusalMessageError>
      ) : null}
    </RefusalMessageContainer>
  ), [
    clientLang.client_quote_details_refuse_reason_placeholder,
    clientLang.client_quote_details_refuse_reason_required,
    clientLang.client_quote_details_refuse_reason_text,
  ]);

  const openRefusalDialog = useCallback((type) => {
    refusalTypeRef.current = type;
    refusalReasonRef.current = '';

    showModal({
      type: 'warning',
      title: type === 'budget'
        ? clientLang.client_quote_details_budget_refuse_title
        : type === 'quotation'
          ? 'Recusar Cotação'
          : clientLang.client_quote_details_estimate_refuse_title,
      message: buildRefusalMessage(false),
      confirmText: clientLang.client_quote_details_refuse_confirm_btn,
      onConfirm: async () => {
        const reason = refusalReasonRef.current.trim();
        if (!reason) {
          setModalConfig((previous) => ({
            ...previous,
            message: buildRefusalMessage(true),
          }));
          return;
        }

        try {
          if (type === 'budget') {
            const budgetId = quoteData?.orcamento?.id;
            if (!budgetId) return;
            await ClientQuoteDetailsService.refuseBudget(
              budgetId,
              reason
            );
          } else if (type === 'quotation') {
            const quoteId = quoteData?.estimativa?.id;
            const userId = localStorage.getItem('userId') || 1;
            if (!quoteId) return;
            await ClientQuoteDetailsService.refuseQuotation(quoteId, userId, reason);
          } else {
            const estimateId = quoteData?.estimativa?.id;
            if (!estimateId) return;
            const userId = localStorage.getItem('userId') || 1;
            await ClientQuoteDetailsService.refuseEstimate(estimateId, userId, reason);
          }

          closeModal();
          await fetchQuoteDetails();
          showModal({
            type: 'info',
            title: type === 'budget'
              ? clientLang.client_quote_details_budget_refuse_success_title
              : type === 'quotation'
                ? 'Cotação Recusada com Sucesso'
                : clientLang.client_quote_details_estimate_refuse_success_title,
            message: clientLang.client_quote_details_refusal_success_message,
            confirmText: clientLang.client_quote_details_ok_btn,
            onConfirm: closeModal,
            onCancel: null,
          });
        } catch (_requestError) {
          closeModal();
          showModal({
            type: 'error',
            title: sharedLang.shared_details_error_label,
            message: type === 'budget'
              ? clientLang.client_quote_details_budget_refuse_error
              : type === 'quotation'
                ? 'Não foi possível recusar a cotação.'
                : clientLang.client_quote_details_refuse_error,
            confirmText: clientLang.client_quote_details_ok_btn,
            onConfirm: closeModal,
            onCancel: null,
          });
        }
      },
    });
  }, [
    buildRefusalMessage,
    clientLang.client_quote_details_budget_refuse_error,
    clientLang.client_quote_details_budget_refuse_success_title,
    clientLang.client_quote_details_budget_refuse_title,
    clientLang.client_quote_details_estimate_refuse_success_title,
    clientLang.client_quote_details_estimate_refuse_title,
    clientLang.client_quote_details_ok_btn,
    clientLang.client_quote_details_refusal_success_message,
    clientLang.client_quote_details_refuse_confirm_btn,
    clientLang.client_quote_details_refuse_error,
    closeModal,
    fetchQuoteDetails,
    quoteData,
    sharedLang.shared_details_error_label,
    showModal,
  ]);

  const handleRefuseEstimate = useCallback(() => {
    openRefusalDialog('estimate');
  }, [openRefusalDialog]);

  const handleAcceptBudget = useCallback(() => {
    const budgetId = quoteData?.orcamento?.id;
    if (!budgetId) return;

    showModal({
      type: 'success',
      title: clientLang.client_quote_details_budget_accept_title,
      message: clientLang.client_quote_details_budget_accept_text,
      confirmText: clientLang.client_quote_details_budget_accept_confirm,
      onConfirm: async () => {
        try {
          await ClientQuoteDetailsService.acceptBudget(budgetId);
          closeModal();
          await fetchQuoteDetails();
          showModal({
            type: 'success',
            title: clientLang.client_quote_details_budget_accept_success_title,
            message: clientLang.client_quote_details_budget_accept_success_text,
            confirmText: clientLang.client_quote_details_ok_btn,
            onConfirm: closeModal,
            onCancel: null,
          });
        } catch (_requestError) {
          closeModal();
          showModal({
            type: 'error',
            title: sharedLang.shared_details_error_label,
            message: clientLang.client_quote_details_budget_accept_error,
            confirmText: clientLang.client_quote_details_ok_btn,
            onConfirm: closeModal,
            onCancel: null,
          });
        }
      },
    });
  }, [
    clientLang.client_quote_details_budget_accept_confirm,
    clientLang.client_quote_details_budget_accept_error,
    clientLang.client_quote_details_budget_accept_success_text,
    clientLang.client_quote_details_budget_accept_success_title,
    clientLang.client_quote_details_budget_accept_text,
    clientLang.client_quote_details_budget_accept_title,
    clientLang.client_quote_details_ok_btn,
    closeModal,
    fetchQuoteDetails,
    quoteData,
    sharedLang.shared_details_error_label,
    showModal,
  ]);

  const handleRefuseBudget = useCallback(() => {
    openRefusalDialog('budget');
  }, [openRefusalDialog]);

  const handleNextStep = useCallback(() => {
    if (isFlowLocked) return;

    if (!validateCurrentStep()) {
      showModal({
        type: 'warning',
        title: clientLang.client_quote_details_warning_title,
        message: getCurrentValidationMessage(),
        confirmText: clientLang.client_quote_details_ok_btn,
        onConfirm: closeModal,
        onCancel: null,
      });
      return;
    }

    setCurrentStep((previous) => Math.min(previous + 1, steps.length - 1));
  }, [
    clientLang.client_quote_details_ok_btn,
    clientLang.client_quote_details_warning_title,
    closeModal,
    getCurrentValidationMessage,
    isFlowLocked,
    showModal,
    steps.length,
    validateCurrentStep,
  ]);

  const handlePrevStep = useCallback(() => {
    setCurrentStep((previous) => Math.max(previous - 1, 0));
  }, []);

  const stepValidation = useMemo(() => ([
    Boolean(quoteData && !isLoading && !error),
    shippingConfirmed,
    Boolean(notaRemessaFile) && hasCheckoutActions(quoteData),
    true,
  ]), [error, hasCheckoutActions, isLoading, notaRemessaFile, quoteData, shippingConfirmed]);

  const isNextDisabled =
    isFlowLocked ||
    currentStep === steps.length - 1 ||
    !stepValidation[currentStep];

  return (
    <ClientQuoteDetailsView
      quoteData={quoteData}
      isLoading={isLoading}
      error={error}
      currentLanguage={currentLanguage}
      steps={steps}
      currentStep={currentStep}
      statusVariant={statusVariant}
      shippingConfirmed={shippingConfirmed}
      notaRemessaFile={notaRemessaFile}
      canShowEstimateActions={canShowEstimateActions}
      canShowBudgetActions={canShowBudgetActions}
      onAcceptEstimate={handleAcceptEstimate}
      onRefuseEstimate={handleRefuseEstimate}
      onAcceptBudget={handleAcceptBudget}
      onRefuseBudget={handleRefuseBudget}
      onRefuseQuotation={() => openRefusalDialog('quotation')}
      onNextStep={handleNextStep}
      onPrevStep={handlePrevStep}
      onToggleShippingConfirmed={setShippingConfirmed}
      onNotaRemessaFileChange={setNotaRemessaFile}
      isFlowLocked={isFlowLocked}
      flowLockMessage={flowLockMessage}
      isNextDisabled={isNextDisabled}
      modalConfig={modalConfig}
      isPostAcceptance={isPostAcceptance}
    />
  );
};

export default withRouter(ClientQuoteDetailsPage);
